/**
 * Shoreline — progressive enhancement only.
 *
 * Two behaviours, both optional: reveal-on-scroll and the bestsellers rail
 * arrows. The design gated section opacity on JS state; here the rail is a
 * native scroll-snap container and reveal is a CSS class, so with JS off or
 * reduced motion on, every section renders at rest and the rail still scrolls.
 */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  // .sl-rev fades a whole section in; .sl-anim only receives .is-in, and the
  // CSS for each component (note tiers, gauge, curves, timeline) decides what
  // that entrance looks like.
  var REVEAL = '.sl-rev, .sl-anim';

  /**
   * Counts a [data-sl-count] figure up from zero. The server renders the
   * final value, so with reduced motion or no JS the number is simply there.
   */
  /**
   * For data-sl-count="auto": splits a figure such as "8 hrs" or "4.98★" into
   * the text before the number, the number, and the text after it, so the
   * count keeps its prefix, suffix and decimal places.
   */
  function countParts(text) {
    var m = String(text).match(/^(\D*?)(\d+(?:\.\d+)?)(.*)$/);
    if (!m) return null;
    var dec = m[2].indexOf('.') > -1 ? m[2].split('.')[1].length : 0;
    return { pre: m[1], target: parseFloat(m[2]), dec: dec, suf: m[3] };
  }

  function countUp(el) {
    if (el.dataset.slCounted) return;
    el.dataset.slCounted = 'true';

    var final = el.dataset.slFinal || el.textContent;
    var parts = el.dataset.slCount === 'auto' ? countParts(final) : null;
    var target = parts ? parts.target : parseFloat(el.dataset.slCount);
    if (reduced || !isFinite(target) || typeof requestAnimationFrame === 'undefined') {
      el.textContent = final;
      return;
    }

    var t0 = null;
    var dur = parts ? 1600 : 2100;
    function step(now) {
      if (t0 === null) t0 = now;
      var k = Math.min(1, (now - t0) / dur);
      var eased = 1 - Math.pow(1 - k, 3);
      if (k >= 1) el.textContent = final;
      else if (parts) el.textContent = parts.pre + (target * eased).toFixed(parts.dec) + parts.suf;
      else el.textContent = (target * eased).toFixed(1);
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function arrive(el) {
    el.classList.add('is-in');
    Array.prototype.forEach.call(el.querySelectorAll('[data-sl-count]'), countUp);
  }

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) arrive(nodes[i]);
  }

  function initReveal() {
    var nodes = document.querySelectorAll(REVEAL);
    if (!nodes.length) return;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      revealAll(nodes);
      return;
    }

    // Counters start from zero only when they are going to animate.
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-count]'), function (c) {
      if (c.dataset.slCounted) return;
      if (!c.dataset.slFinal) c.dataset.slFinal = c.textContent;
      var p = c.dataset.slCount === 'auto' ? countParts(c.dataset.slFinal) : null;
      if (c.dataset.slCount === 'auto' && !p) return;
      c.textContent = p ? p.pre + (0).toFixed(p.dec) + p.suf : '0';
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          arrive(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);

    // A backgrounded or prerendered document never fires the observer; make
    // sure nothing stays invisible once it becomes visible.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) revealAll(document.querySelectorAll(REVEAL));
    });
  }

  /* ------------------------------------------------------------ family tilt */

  /**
   * Fans the family tiles: outer tiles yaw outward, centre tiles lift forward.
   * Values come from the tile's index so the section works with any count.
   */
  function initFamilyTilt() {
    var grids = document.querySelectorAll('.sl-fam--tilt .sl-fam__grid');

    Array.prototype.forEach.call(grids, function (grid) {
      var cards = grid.querySelectorAll('.sl-fam__card');
      var n = cards.length;
      if (!n) return;

      Array.prototype.forEach.call(cards, function (card, i) {
        var t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
        card.style.setProperty('--fam-yaw', (-t * 15).toFixed(2) + 'deg');
        card.style.setProperty('--fam-lift', ((1 - Math.abs(t)) * 22).toFixed(1) + 'px');
      });
    });
  }

  /* -------------------------------------------------------------- carousel */

  function initCarousels() {
    var rails = document.querySelectorAll('[data-sl-carousel]');

    Array.prototype.forEach.call(rails, function (rail) {
      var viewport = rail.querySelector('.sl-best__viewport');
      var prev = rail.querySelector('[data-sl-prev]');
      var next = rail.querySelector('[data-sl-next]');
      if (!viewport || !prev || !next || rail.dataset.slRailBound) return;
      rail.dataset.slRailBound = 'true';
      var progress = rail.querySelector('[data-sl-progress]');

      function step() {
        var slide = viewport.querySelector('.sl-best__slide');
        if (!slide) return viewport.clientWidth;
        var rect = slide.getBoundingClientRect();
        var style = window.getComputedStyle(slide);
        var margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        var per = Math.max(1, Math.floor(viewport.clientWidth / (rect.width + margin)));
        return (rect.width + margin) * per;
      }

      function sync() {
        // 2px tolerance absorbs sub-pixel scroll positions at the extremes.
        var max = viewport.scrollWidth - viewport.clientWidth;
        prev.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= max - 2;

        if (progress) {
          // The thumb is as wide as the visible share of the rail and travels
          // across the rest of the bar as it scrolls.
          var share = viewport.scrollWidth ? Math.min(1, viewport.clientWidth / viewport.scrollWidth) : 1;
          var ratio = max > 0 ? viewport.scrollLeft / max : 0;
          progress.style.setProperty('--best-thumb', (share * 100).toFixed(2) + '%');
          progress.style.setProperty('--best-pos', (((1 - share) / share) * ratio * 100).toFixed(2) + '%');
        }
      }

      function scrollBy(dir) {
        viewport.scrollBy({
          left: dir * step(),
          behavior: reduced ? 'auto' : 'smooth'
        });
      }

      /* Autoplay: advances every N seconds, wraps to the start, and stops for
         good once the shopper takes over. It also waits while the rail is off
         screen, hovered, focused or the tab is hidden.

         Touch screens never autoplay. There is no hover there to pause it, so
         the rail would slide out from under a thumb already reaching for a
         bottle — the tap then lands on the next card, or on nothing. */
      var canHover = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var seconds = parseFloat(rail.dataset.slAutoplay || '0');
      var takenOver = false;
      var hovering = false;
      var onScreen = false;
      if (seconds > 0 && !reduced && canHover) {
        var timer = window.setInterval(function () {
          if (takenOver || hovering || !onScreen || document.hidden) return;
          if (rail.contains(document.activeElement)) return;
          var max = viewport.scrollWidth - viewport.clientWidth;
          if (max <= 2) return;
          if (viewport.scrollLeft >= max - 2) viewport.scrollTo({ left: 0, behavior: 'smooth' });
          else scrollBy(1);
        }, seconds * 1000);

        var stop = function () {
          takenOver = true;
          window.clearInterval(timer);
          // A smooth scroll still in flight swallows the tap that stopped it —
          // that is why tapping a moving card opened nothing. Freeze the rail
          // where it stands so this first tap reaches the card's link.
          var x = viewport.scrollLeft;
          viewport.style.scrollBehavior = 'auto';
          viewport.scrollLeft = x;
          viewport.style.scrollBehavior = '';
        };
        rail.addEventListener('pointerenter', function () { hovering = true; });
        rail.addEventListener('pointerleave', function () { hovering = false; });
        // pointerdown covers mouse, touch and pen: the rail stops the moment a
        // shopper reaches for a card, so it can never move out from under them.
        rail.addEventListener('pointerdown', stop, { passive: true });
        viewport.addEventListener('touchstart', stop, { passive: true });
        viewport.addEventListener('wheel', function (e) { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop(); }, { passive: true });

        if (typeof IntersectionObserver !== 'undefined') {
          new IntersectionObserver(function (entries) {
            onScreen = entries[0].isIntersecting;
          }, { threshold: 0.35 }).observe(viewport);
        } else {
          onScreen = true;
        }

        prev.addEventListener('click', stop);
        next.addEventListener('click', stop);
      }

      /* Card tilt towards the pointer (showcase only, mouse only). */
      if (rail.classList.contains('sl-best--showcase') && !reduced &&
          window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        viewport.addEventListener('pointermove', function (e) {
          var card = e.target.closest && e.target.closest('.sl-card');
          if (!card) return;
          var r = card.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5;
          var y = (e.clientY - r.top) / r.height - 0.5;
          card.style.setProperty('--ry', (x * 10).toFixed(2) + 'deg');
          card.style.setProperty('--rx', (-y * 8).toFixed(2) + 'deg');
        });
        viewport.addEventListener('pointerout', function (e) {
          var card = e.target.closest && e.target.closest('.sl-card');
          if (card && !card.contains(e.relatedTarget)) {
            card.style.removeProperty('--rx');
            card.style.removeProperty('--ry');
          }
        });
      }

      prev.addEventListener('click', function () { scrollBy(-1); });
      next.addEventListener('click', function () { scrollBy(1); });
      viewport.addEventListener('scroll', sync, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(viewport);
      } else {
        window.addEventListener('resize', sync);
      }

      sync();
    });
  }

  /* --------------------------------------------------------------- filters */

  /**
   * Submits the collection filter form as soon as a control changes, so the
   * page behaves like a filtered view rather than a form to fill in. The
   * form is a plain GET, so without this it still works via the Apply
   * button shown in the <noscript> block.
   */
  function initFilters() {
    var form = document.getElementById('ShorelineFilters');
    if (!form || form.dataset.slBound) return;
    form.dataset.slBound = 'true';

    form.addEventListener('change', function () { form.submit(); });

    // The sort <select> lives outside the form and is bound by form=; Safari
    // does not support that attribute, so submit it explicitly.
    var sort = document.getElementById('ShorelineSort');
    if (sort && !form.contains(sort)) {
      sort.addEventListener('change', function () {
        var hidden = form.querySelector('input[name="sort_by"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          form.appendChild(hidden);
        }
        hidden.value = sort.value;
        form.submit();
      });
    }
  }

  /* ------------------------------------------------------------- product */

  /** Quantity stepper. The input itself stays usable if this never runs. */
  function initQty() {
    var input = document.getElementById('ShorelineQty');
    if (!input) return;

    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-qty]'), function (btn) {
      if (btn.dataset.slBound) return;
      btn.dataset.slBound = 'true';

      btn.addEventListener('click', function () {
        var min = parseInt(input.min, 10) || 1;
        var next = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.slQty, 10);
        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /** Thumbnail click swaps the hero image source. */
  function initGallery() {
    var hero = document.getElementById('ShorelineHero');
    var thumbs = document.querySelectorAll('[data-sl-thumb]');
    if (!hero || !thumbs.length) return;

    Array.prototype.forEach.call(thumbs, function (thumb) {
      if (thumb.dataset.slBound) return;
      thumb.dataset.slBound = 'true';

      thumb.addEventListener('click', function () {
        var full = thumb.dataset.full;
        if (!full) return;
        hero.src = full;
        // srcset would override the src we just set.
        hero.removeAttribute('srcset');
        Array.prototype.forEach.call(thumbs, function (t) { t.removeAttribute('aria-current'); });
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ------------------------------------------------------------ coverflow */

  var STARS = '★★★★★';

  /** Reads the Judge.me reviews already rendered on the page into card data. */
  function ugcFromJudgeme(root) {
    var max = parseInt(root.dataset.max, 10) || 8;
    var fallback = root.dataset.fallbackImg || '';
    var cards = [];

    Array.prototype.forEach.call(document.querySelectorAll('#judgeme_product_reviews .jdgm-rev'), function (rev) {
      var body = rev.querySelector('.jdgm-rev__body');
      var text = body ? body.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text) return;

      var author = rev.querySelector('.jdgm-rev__author');
      var link = rev.querySelector('.jdgm-rev__pic-link');
      var pic = rev.querySelector('.jdgm-rev__pic-img');
      var img = (link && link.getAttribute('href')) || (pic && (pic.getAttribute('data-src') || pic.getAttribute('src'))) || '';
      var rating = rev.querySelector('.jdgm-rev__rating');

      cards.push({
        text: text,
        author: author ? author.textContent.trim() : '',
        img: img || fallback,
        photo: !!img,
        score: Math.max(1, Math.min(5, parseInt(rating && rating.getAttribute('data-score'), 10) || 5)),
        verified: rev.getAttribute('data-verified-buyer') === 'true'
      });
    });

    // Buyer photos lead; the product shot stands in for text-only reviews.
    cards.sort(function (a, b) { return (b.photo ? 1 : 0) - (a.photo ? 1 : 0); });
    return cards.slice(0, max);
  }

  /** Builds a card with DOM APIs, so review text is never parsed as HTML. */
  function buildCard(c) {
    var fig = document.createElement('figure');
    fig.className = 'sl-ugc__card';

    var media = document.createElement('div');
    media.className = 'sl-ugc__media';
    if (c.img) {
      var im = document.createElement('img');
      im.src = c.img;
      im.alt = c.photo ? 'Photo from ' + (c.author || 'a buyer') : '';
      im.loading = 'lazy';
      media.appendChild(im);
    }

    var body = document.createElement('div');
    body.className = 'sl-ugc__body';
    var stars = document.createElement('span');
    stars.className = 'sl-ugc__stars';
    stars.textContent = STARS.slice(0, c.score);
    stars.setAttribute('aria-label', c.score + ' out of 5 stars');
    var quote = document.createElement('blockquote');
    quote.className = 'sl-ugc__quote';
    quote.textContent = c.text;
    var who = document.createElement('figcaption');
    who.className = 'sl-ugc__who';
    who.textContent = (c.author || 'Buyer') + (c.verified ? ' · Verified' : '');

    body.appendChild(stars);
    body.appendChild(quote);
    body.appendChild(who);
    fig.appendChild(media);
    fig.appendChild(body);
    return fig;
  }

  /** 5★ → 1★ bars from Judge.me's histogram; hidden when there is none. */
  function ugcBars(root) {
    var host = root.querySelector('[data-sl-ugc-bars]');
    if (!host || host.childNodes.length) return;
    var rows = document.querySelectorAll('#judgeme_product_reviews .jdgm-histogram__row[data-rating]');

    Array.prototype.forEach.call(rows, function (r) {
      var rating = r.getAttribute('data-rating');
      if (!rating || rating === 'null') return;
      var row = document.createElement('div');
      row.className = 'sl-ugc__bar';
      var label = document.createElement('span');
      label.textContent = rating + '★';
      var track = document.createElement('span');
      track.className = 'sl-ugc__track';
      var fill = document.createElement('span');
      fill.className = 'sl-ugc__fill';
      fill.style.setProperty('--w', (parseFloat(r.getAttribute('data-percentage')) || 0) + '%');
      track.appendChild(fill);
      var n = document.createElement('span');
      n.className = 'sl-ugc__n';
      n.textContent = r.getAttribute('data-frequency') || '0';
      row.appendChild(label);
      row.appendChild(track);
      row.appendChild(n);
      host.appendChild(row);
    });

    if (!host.childNodes.length) host.hidden = true;
  }

  /**
   * The design's coverflow: the active card faces the viewer, neighbours
   * swing back and fade (same maths as the design canvas).
   */
  function initCoverflow() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-ugc]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var stage = root.querySelector('[data-sl-ugc-stage]');
      var dotsHost = root.querySelector('[data-sl-ugc-dots]');
      if (!stage) return;

      if (root.getAttribute('data-source') === 'judgeme') {
        ugcFromJudgeme(root).forEach(function (c) { stage.appendChild(buildCard(c)); });
      }
      ugcBars(root);

      var cards = stage.querySelectorAll('.sl-ugc__card');
      if (!cards.length) return;
      root.hidden = false;

      var n = cards.length;
      var active = 0;
      var dots = [];
      var swiped = false;

      function layout() {
        var tallest = 0;
        Array.prototype.forEach.call(cards, function (card, i) {
          var d = i - active;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;
          var a = Math.abs(d);
          var sign = d < 0 ? -1 : 1;
          var x = -50 + d * 82;
          var scale = a === 0 ? 1 : Math.max(0.74, 1 - a * 0.13);
          var rot = a === 0 ? 0 : sign * Math.min(38, 26 + a * 6);
          var z = a === 0 ? 0 : -110 - (a - 1) * 90;

          card.style.transform = 'translateX(' + x + '%) translateZ(' + z + 'px) rotateY(' + -rot + 'deg) scale(' + scale.toFixed(3) + ')';
          card.style.opacity = a > 2 ? '0' : a === 0 ? '1' : '0.42';
          card.style.filter = a === 0 ? 'none' : 'blur(1.4px)';
          card.style.zIndex = String(100 - a * 10);
          card.style.boxShadow = a === 0 ? '0 26px 60px -22px rgba(22,24,28,.42)' : '0 12px 30px -18px rgba(22,24,28,.28)';
          card.style.cursor = a === 0 ? 'default' : 'pointer';
          card.setAttribute('aria-hidden', a === 0 ? 'false' : 'true');
          tallest = Math.max(tallest, card.offsetHeight);
        });
        if (tallest) stage.style.height = tallest + 28 + 'px';
        dots.forEach(function (dot, i) { dot.setAttribute('aria-current', i === active ? 'true' : 'false'); });
      }

      function go(i) {
        active = ((i % n) + n) % n;
        layout();
      }

      Array.prototype.forEach.call(cards, function (card, i) {
        card.addEventListener('click', function () {
          if (swiped) { swiped = false; return; }
          if (i !== active) go(i);
        });
        if (dotsHost && n > 1) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'sl-ugc__dot';
          dot.setAttribute('aria-label', 'Show review ' + (i + 1));
          dot.addEventListener('click', function () { go(i); });
          dotsHost.appendChild(dot);
          dots.push(dot);
        }
      });

      var prev = root.querySelector('[data-sl-ugc-prev]');
      var next = root.querySelector('[data-sl-ugc-next]');
      if (n < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
      }
      if (prev) prev.addEventListener('click', function () { go(active - 1); });
      if (next) next.addEventListener('click', function () { go(active + 1); });

      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
      });

      var x0 = null;
      stage.addEventListener('pointerdown', function (e) { x0 = e.clientX; });
      stage.addEventListener('pointerup', function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) {
          swiped = true;
          go(active + (dx < 0 ? 1 : -1));
        }
      });

      window.addEventListener('resize', layout);
      Array.prototype.forEach.call(stage.querySelectorAll('img'), function (im) {
        if (!im.complete) im.addEventListener('load', layout);
      });
      layout();
    });
  }

  /* ---------------------------------------------------------- tier focus */

  /**
   * Touch screens have no hover, so the note tier crossing the middle of the
   * viewport gets .is-focus and lifts out of the stack as the shopper scrolls.
   */
  function initTierFocus() {
    if (reduced || typeof IntersectionObserver === 'undefined') return;
    if (!window.matchMedia('(hover: none), (max-width: 1040px)').matches) return;

    var tiers = document.querySelectorAll('.sl-pyr__tier');
    if (!tiers.length) return;

    // Only one tier lifts at a time: the one whose centre is nearest the
    // middle of the screen, while any tier crosses the central band.
    var inBand = [];
    function pick() {
      var mid = window.innerHeight / 2, best = null, bestD = Infinity;
      inBand.forEach(function (t) {
        var r = t.getBoundingClientRect();
        var d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = t; }
      });
      Array.prototype.forEach.call(tiers, function (t) { t.classList.toggle('is-focus', t === best); });
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          var i = inBand.indexOf(e.target);
          if (e.isIntersecting && i < 0) inBand.push(e.target);
          if (!e.isIntersecting && i > -1) inBand.splice(i, 1);
        });
        pick();
      },
      { rootMargin: '-35% 0px -35% 0px' }
    );
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!inBand.length || ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; pick(); });
    }, { passive: true });
    Array.prototype.forEach.call(tiers, function (t) {
      if (t.dataset.slFocus) return;
      t.dataset.slFocus = 'true';
      io.observe(t);
    });
  }

  /* ------------------------------------------------------------------ boot */

  /* ------------------------------------------------------- sticky buy bar */

  /**
   * Shows the sticky bar while the product's own Add to bag button is off
   * screen (and the footer isn't in view). Its button clicks the real one, so
   * Horizon's add-to-cart flow and the cart drawer run unchanged.
   */
  function initStickyBuy() {
    var bars = document.querySelectorAll('[data-sl-sticky]');

    Array.prototype.forEach.call(bars, function (bar) {
      if (bar.dataset.slBound) return;
      bar.dataset.slBound = 'true';

      var section = bar.closest('.shopify-section') || document;
      var buy = section.querySelector('.sl-buy');
      var realBtn = section.querySelector('[data-sl-atc]');
      var btn = bar.querySelector('[data-sl-sticky-btn]');

      // A theme-editor re-render leaves the previous bar in <body>.
      Array.prototype.forEach.call(document.querySelectorAll('body > [data-sl-sticky]'), function (old) {
        if (old !== bar && old.dataset.section === bar.dataset.section) old.remove();
      });
      document.body.appendChild(bar);

      if (btn && realBtn) {
        btn.addEventListener('click', function () {
          if (!realBtn.disabled) realBtn.click();
        });
      }
      if (!buy || typeof IntersectionObserver === 'undefined') return;

      var buyInView = true;
      var footerInView = false;
      function update() {
        var show = !buyInView && !footerInView;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
      }

      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { buyInView = e.isIntersecting; });
        update();
      }).observe(buy);

      var footer = document.querySelector('.shopify-section-group-footer-group, footer');
      if (footer) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { footerInView = e.isIntersecting; });
          update();
        }).observe(footer);
      }
    });
  }

  /* ------------------------------------------------------ recommendations */

  /**
   * "Layer it with": fetches the section through Shopify's recommendations
   * endpoint, drops the product being viewed, keeps the configured number of
   * cards, then animates them in when they scroll into view.
   */
  function initRecs() {
    var roots = document.querySelectorAll('[data-sl-recs]');

    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.slLoaded || !root.dataset.url || !root.dataset.productId) return;
      root.dataset.slLoaded = 'true';

      fetch(root.dataset.url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sl-recs]');
          if (!fresh || !fresh.innerHTML.trim()) return;

          root.innerHTML = fresh.innerHTML;

          var limit = parseInt(root.dataset.limit, 10) || 4;
          var shown = 0;
          Array.prototype.forEach.call(root.querySelectorAll('.sl-recs__item'), function (item) {
            if (item.dataset.recId === root.dataset.productId || shown >= limit) {
              item.remove();
              return;
            }
            item.style.setProperty('--i', shown);
            shown++;
          });
          if (!shown) return;
          root.hidden = false;

          var targets = root.querySelectorAll('.sl-anim, .sl-rev');
          if (reduced || typeof IntersectionObserver === 'undefined') {
            revealAll(targets);
            return;
          }
          var io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (e) {
                if (!e.isIntersecting) return;
                arrive(e.target);
                io.unobserve(e.target);
              });
            },
            { threshold: 0.15 }
          );
          Array.prototype.forEach.call(targets, function (t) { io.observe(t); });
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------- size selection */

  /**
   * The size buttons are not the form's variant input — Horizon's product form
   * needs a single [ref="variantId"] field to take over the submit. Choosing a
   * size writes into that field and updates the price and the button.
   */
  function initSizePicker() {
    var inputs = document.querySelectorAll('[data-sl-variant-input]');

    Array.prototype.forEach.call(inputs, function (hidden) {
      var form = hidden.closest('form');
      if (!form || form.dataset.slSize) return;
      form.dataset.slSize = 'true';

      form.addEventListener('change', function (event) {
        var radio = event.target.closest ? event.target.closest('[data-sl-size]') : null;
        if (!radio) return;

        hidden.value = radio.value;

        var money = radio.dataset.price || '';
        var price = document.getElementById('ShorelinePrice');
        if (price && money) price.textContent = money;

        var button = form.querySelector('[data-sl-atc]');
        if (button && button.dataset.label) {
          button.textContent =
            button.dataset.showPrice === 'true' && money
              ? button.dataset.label + ' — ' + money
              : button.dataset.label;
        }

        // The sticky bar lives in <body>, so it is updated by attribute.
        var variantRow = radio.closest('.sl-variant');
        var sizeText = variantRow && variantRow.querySelector('.sl-variant__ml');
        Array.prototype.forEach.call(document.querySelectorAll('[data-sl-sticky-price]'), function (el) {
          if (money) el.textContent = money;
        });
        Array.prototype.forEach.call(document.querySelectorAll('[data-sl-sticky-size]'), function (el) {
          if (sizeText) el.textContent = sizeText.textContent;
        });
      });
    });
  }

  /* ------------------------------------------------------- note-hour bars */

  /**
   * The hour-by-hour bars. Every column already carries its own numbers, so
   * this only moves them into the readout — the chart and the readout are both
   * correct with the script switched off.
   */
  function initBars() {
    var charts = document.querySelectorAll('[data-sl-bars]');

    Array.prototype.forEach.call(charts, function (chart) {
      if (chart.dataset.slBound) return;
      chart.dataset.slBound = 'true';

      var cols = chart.querySelectorAll('.sl-bars__col');
      if (!cols.length) return;

      var when = chart.querySelector('[data-sl-read-when]');
      var strength = chart.querySelector('[data-sl-read-strength]');
      var lead = chart.querySelector('[data-sl-read-lead]');
      var meters = chart.querySelectorAll('[data-sl-mix]');
      var pcts = chart.querySelectorAll('[data-sl-pct]');
      var home = cols[parseInt(chart.dataset.default, 10) || 0] || cols[0];
      var pinned = null;

      function read(col) {
        if (!col) return;
        if (when) when.textContent = col.dataset.when || '';
        if (strength) strength.textContent = (col.dataset.strength || '0') + '% strength';
        if (lead) {
          lead.textContent = col.dataset.lead || '';
          lead.style.setProperty('--c', col.dataset.leadC || '');
        }
        Array.prototype.forEach.call(meters, function (m) {
          m.style.width = (col.dataset['p' + m.dataset.slMix] || 0) + '%';
        });
        Array.prototype.forEach.call(pcts, function (p) {
          p.textContent = (col.dataset['p' + p.dataset.slPct] || 0) + '%';
        });
        Array.prototype.forEach.call(cols, function (c) { c.classList.toggle('is-on', c === col); });
      }

      Array.prototype.forEach.call(cols, function (col) {
        // Hover and keyboard focus preview an hour; a click or tap keeps it,
        // which is the only way to hold one on a touch screen.
        col.addEventListener('pointerenter', function () { if (!pinned) read(col); });
        col.addEventListener('focus', function () { read(col); });
        // A click always keeps that hour — never a toggle, so a double tap or
        // a stray second click cannot bounce the reading back to the start.
        col.addEventListener('click', function () { pinned = col; read(col); });
      });

      chart.addEventListener('pointerleave', function () { read(pinned || home); });
    });
  }

  /* ------------------------------------------------------------ scroll hero */

  /**
   * Publishes four numbers and lets CSS do everything with them: --p for the
   * whole pinned scroll, and --a1/--a2/--a3 for the three acts, each easing
   * from 0 to 1 across its own slice of it. One rAF-throttled scroll listener
   * that sleeps while the hero is off screen; no layout is read per layer.
   */
  function initHero3() {
    var heroes = document.querySelectorAll('[data-sl-hero3]');

    // Act boundaries as fractions of the pinned scroll.
    var ACTS = [[0, 0.3], [0.26, 0.66], [0.62, 1]];

    function slice(p, from, to) {
      return Math.min(1, Math.max(0, (p - from) / (to - from)));
    }
    /** Ease-in-out, so an act settles rather than stopping dead. */
    function ease(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    Array.prototype.forEach.call(heroes, function (hero) {
      if (hero.dataset.slHero3) return;
      hero.dataset.slHero3 = 'true';
      if (reduced) return;

      var stage = hero.querySelector('[data-sl-hero3-stage]');
      var film = hero.querySelector('[data-sl-hero3-film]');
      var onScreen = true;
      var ticking = false;

      /**
       * The film is a luxury, so it has to earn its download: skip it for
       * anyone who asked for less motion, anyone on Data Saver, and anyone on a
       * slow connection. They keep the still plate, which has already loaded.
       */
      function filmIsWelcome() {
        if (!film || !film.dataset.src) return false;
        var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (c && c.saveData) return false;
        if (c && /(^|-)(2g|slow-2g)$/.test(c.effectiveType || '')) return false;
        return true;
      }

      function startFilm() {
        if (!filmIsWelcome()) return;
        if (!film.dataset.slLoaded) {
          film.dataset.slLoaded = 'true';
          film.src = film.dataset.src;
          // Only show it once frames are actually running, so a stalled load
          // never blanks the plate. is-film also stands the ingredient ring
          // down — the film is already carrying the ingredients.
          film.addEventListener('playing', function () {
            film.classList.add('is-live');
            hero.classList.add('is-film');
          }, { once: true });
        }
        var playing = film.play();
        if (playing && playing.catch) playing.catch(function () {});
      }

      function frame() {
        ticking = false;
        var r = hero.getBoundingClientRect();
        // The pin is one viewport tall, so the travel is what is left of the
        // section once that is taken off.
        var travel = Math.max(1, r.height - window.innerHeight);
        var p = Math.min(1, Math.max(0, -r.top / travel));
        hero.style.setProperty('--p', p.toFixed(4));
        for (var i = 0; i < ACTS.length; i++) {
          hero.style.setProperty('--a' + (i + 1), ease(slice(p, ACTS[i][0], ACTS[i][1])).toFixed(4));
        }
      }

      function onScroll() {
        if (ticking || !onScreen) return;
        ticking = true;
        requestAnimationFrame(frame);
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      if (typeof IntersectionObserver !== 'undefined') {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen) {
            onScroll();
            startFilm();
          } else if (film && film.dataset.slLoaded) {
            // Nothing decodes while it is off screen.
            film.pause();
          }
        }).observe(hero);
      } else if (film) {
        startFilm();
      }

      // A backgrounded tab should not be decoding video either.
      document.addEventListener('visibilitychange', function () {
        if (!film || !film.dataset.slLoaded) return;
        if (document.hidden) film.pause();
        else if (onScreen) startFilm();
      });

      // Parallax tilt, mouse only — a touch screen has nothing to point with.
      if (stage && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        hero.addEventListener('pointermove', function (e) {
          var b = hero.getBoundingClientRect();
          var x = (e.clientX - b.left) / b.width - 0.5;
          var y = (e.clientY - b.top) / b.height - 0.5;
          stage.style.setProperty('--ry', (x * 9).toFixed(2) + 'deg');
          stage.style.setProperty('--rx', (-y * 6).toFixed(2) + 'deg');
        });
        hero.addEventListener('pointerleave', function () {
          stage.style.removeProperty('--rx');
          stage.style.removeProperty('--ry');
        });
      }

      frame();
    });
  }

  function init() {
    initReveal();
    initHero3();
    initBars();
    initSizePicker();
    initStickyBuy();
    initRecs();
    initFamilyTilt();
    initCarousels();
    initFilters();
    initQty();
    initGallery();
    initTierFocus();
    if (typeof initCoverflow === 'function') initCoverflow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup without a page load.
  document.addEventListener('shopify:section:load', init);
})();
